
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import { BuildTypeDetails } from '@/@types/responses/build_type/BuildTypeDetails'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import { BuildTypeService } from '@/services/buildType.service'
import Checkbox from '@/components/shared/Checkbox.vue'
import { BuildTypeMessage } from '@/@types/messages/BuildTypeMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'InventoryItem',
  components: {
    AuditInformation,
    Checkbox,
    LoadingPulse,
    LockingButton,
    TextInput
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const buildTypeService = inject(PROVIDER_CONSTANTS.buildTypeService) as BuildTypeService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      buildTypeService,
      toast
    }
  },
  data() {
    let buildType: BuildTypeDetails|undefined
    let unchangedBuildType: BuildTypeDetails|undefined

    return {
      buildType,
      loading: true,
      saving: false,
      unchangedBuildType
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.buildType
      const b = this.unchangedBuildType

      return (a?.name !== b?.name || a?.cartridgeRequired !== b?.cartridgeRequired) && a?.name.trim() !== ''
    },
    titleText(): string {
      return !this.buildType || this.buildType.id === 0 ? 'Create Build Type' : 'Edit Build Type'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    if (this.id > 0) {
      this.buildTypeService.getBuildType(this.id).then(buildType => {
        this.buildType = buildType
        this.unchangedBuildType = JSON.parse(JSON.stringify(buildType))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build type. Try reloading the page.')
        }
      })
    } else {
      this.buildType = new BuildTypeDetails(0)
      this.unchangedBuildType = new BuildTypeDetails(0)
      this.loading = false
    }
  },
  methods: {
    onSaveBuildTypeClicked(): void {
      if (!this.buildType) return

      this.saving = true

      const buildTypeMessage = new BuildTypeMessage(this.buildType.cartridgeRequired, this.buildType.name)
      if (this.buildType.id === 0)
        this.buildTypeService.createBuildType(buildTypeMessage).then(objectCreatedResponse => {
          this.$router.replace(`/build-type/${objectCreatedResponse.id}`)
          this.saving = false
          this.toast.success('The build type was created successfully.')
          this.$router.push('/build-types')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the build type. Please try again.')
          }
        })
      else
        this.buildTypeService.updateBuildType(this.id, buildTypeMessage).then(() => {
          this.toast.success('The build type was updated successfully.')
          this.saving = false
          this.$router.push('/build-types')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the build type. Please try again.')
          }
        })
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.buildType)
        document.title = `${this.buildType.name} - Short Action Customs, LLC`
    }
  }
})
